import {
  Machine,
  MachineLatestCtrDatum,
  MachineLatestRoutineDatum,
  MachineTelemtryDatum,
  MachineVariantDatum,
} from '../interfaces/Machine.types';
import {
  CV50_FACELIFT_FALLBACK_IMAGE,
  CV50_FACELIFT_MATERIAL_NUMBER,
  MACHINE_IMAGE_MAP,
  NO_PICTURE_URL_MARKER,
} from './constants';
import { MachinesReport } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { RobotUtils } from 'app/utils/robot/RobotUtils';

export class MachineUtils {
  public static getFullName = ({ type, name }: Machine): string => [type?.name, name].filter(Boolean).join(' - ');

  public static resolveMachineThumbnailUrl = (
    machineId: string,
    materialNumber: string,
    machinesVariantData: MachineVariantDatum[]
  ): string | undefined => {
    const machineImageUrl = MACHINE_IMAGE_MAP[materialNumber];
    if (machineImageUrl) {
      return machineImageUrl;
    }

    const machineVariantDatum = machinesVariantData.find(
      (machineVariant: MachineVariantDatum): boolean => machineVariant.id === machineId
    );

    return machineVariantDatum?.variant?.picture?.thumbnail ?? undefined;
  };

  public static buildThumbnailUrlsByMachineId = (
    machines: Pick<MachinesReport, 'machineId' | 'materialNumber'>[],
    machinesVariantData: MachineVariantDatum[]
  ): Record<string, string> =>
    machines.reduce<Record<string, string>>((thumbnailUrlsByMachineId, machine) => {
      const thumbnailUrl = MachineUtils.resolveMachineThumbnailUrl(
        machine.machineId,
        machine.materialNumber,
        machinesVariantData
      );

      if (!thumbnailUrl) {
        return thumbnailUrlsByMachineId;
      }

      return {
        ...thumbnailUrlsByMachineId,
        [machine.machineId]: thumbnailUrl,
      };
    }, {});

  public static mergeMachineWithVariantData(
    machineWithoutVariantData: Machine,
    machinesVariantData: MachineVariantDatum[]
  ): Machine {
    const machineImageUrl = MACHINE_IMAGE_MAP[machineWithoutVariantData.materialNumber];
    if (machineImageUrl) {
      return {
        ...machineWithoutVariantData,
        variant: {
          id: machineWithoutVariantData.id,
          materialNumber: machineWithoutVariantData.materialNumber,
          picture: {
            thumbnail: machineImageUrl,
            product: machineImageUrl,
            overview: machineImageUrl,
          },
        },
      };
    }

    const machineVariantDatum = machinesVariantData.find(
      (machineVariant: MachineVariantDatum): boolean => machineVariant.id === machineWithoutVariantData.id
    );

    const isCV50Facelift =
      machineWithoutVariantData.materialNumber === CV50_FACELIFT_MATERIAL_NUMBER &&
      RobotUtils.getRobotType(machineWithoutVariantData.type?.name) === 'CV50';
    const hasNoPicture =
      !machineVariantDatum?.variant?.picture?.thumbnail ||
      machineVariantDatum.variant.picture.thumbnail.includes(NO_PICTURE_URL_MARKER);

    if (isCV50Facelift && hasNoPicture) {
      return {
        ...machineWithoutVariantData,
        variant: {
          id: machineWithoutVariantData.id,
          materialNumber: machineWithoutVariantData.materialNumber,
          picture: CV50_FACELIFT_FALLBACK_IMAGE,
        },
      };
    }

    if (!machineVariantDatum?.variant) return machineWithoutVariantData;

    return {
      ...machineWithoutVariantData,
      variant: machineVariantDatum.variant,
    };
  }

  public static mergeMachineWithTelemetryData(
    machineWithoutTelemetryData: Machine,
    machineTelemetryData: MachineTelemtryDatum[]
  ): Machine {
    const machineTelemetryDatum = machineTelemetryData.find((mtd: any) => mtd.id === machineWithoutTelemetryData.id);

    if (!machineTelemetryDatum?.states) return machineWithoutTelemetryData;

    return {
      ...machineWithoutTelemetryData,
      states: machineTelemetryDatum.states,
    };
  }

  public static mergeMachineWithLatestCtrData(
    machineWithoutLatestCtrData: Machine,
    machineLatestCtrData: MachineLatestCtrDatum[]
  ): Machine {
    const machineLatestCtrDatum = machineLatestCtrData.find(
      (mlctrd: any) => mlctrd.id === machineWithoutLatestCtrData.id
    );

    if (!machineLatestCtrDatum?.cleaningTaskReportLatestGet) return machineWithoutLatestCtrData;

    return {
      ...machineWithoutLatestCtrData,
      cleaningTaskReportLatestGet: machineLatestCtrDatum.cleaningTaskReportLatestGet,
    };
  }

  public static mergeMachineWithLatestRoutineData(
    machineWithoutLatestRoutineData: Machine,
    machineLatestRoutineData: MachineLatestRoutineDatum[]
  ): Machine {
    const machineLatestRoutineDatum = machineLatestRoutineData.find(
      (mlrd: any) => mlrd.id === machineWithoutLatestRoutineData.id
    );

    if (!machineLatestRoutineDatum?.latestRoutine) return machineWithoutLatestRoutineData;

    return {
      ...machineWithoutLatestRoutineData,
      latestRoutine: machineLatestRoutineDatum.latestRoutine,
    };
  }
}
